import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ApiService from '../services/api';

const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const fetchProfile = useCallback(async (force = false) => {
    // Only fetch if not initialized or forced
    if (initialized && !force) {
      return profile;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getProfile();
      
      if (response.status === 200) {
        setProfile(response.data);
        // Also update localStorage for backward compatibility
        localStorage.setItem('user_profile', JSON.stringify(response.data));
        setInitialized(true);
        return response.data;
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError(err.message);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [initialized, profile]);

  // Fetch profile on mount
  useEffect(() => {
    // Check if user is logged in before fetching
    const token = localStorage.getItem(import.meta.env.VITE_JWT_STORAGE_KEY || 'accessToken');
    if (token && !initialized) {
      fetchProfile();
    } else if (!token) {
      setLoading(false);
      setInitialized(true);
    }
  }, [fetchProfile, initialized]);

  // Listen for login events
  useEffect(() => {
    const handleLogin = () => {
      setInitialized(false);
      fetchProfile(true);
    };

    const handleLogout = () => {
      setProfile(null);
      setInitialized(false);
      setLoading(false);
    };

    window.addEventListener('userLoggedIn', handleLogin);
    window.addEventListener('userLoggedOut', handleLogout);

    return () => {
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('userLoggedOut', handleLogout);
    };
  }, [fetchProfile]);

  const refresh = useCallback(() => {
    return fetchProfile(true);
  }, [fetchProfile]);

  const clearProfile = useCallback(() => {
    setProfile(null);
    setInitialized(false);
    localStorage.removeItem('user_profile');
  }, []);

  const value = {
    profile,
    loading,
    error,
    refresh,
    clearProfile,
    initialized,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

// Hook to use profile context
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export default ProfileContext;
